<template>
  <div class="musubi-page musubi-page--lists">
    <header class="musubi-page__head">
      <h1>Mailing lists</h1>
      <p class="text-muted">
        Addressable audiences. A list is a deduped collection of subscribers —
        segments, campaigns, and drips target lists. People join by submitting a
        form, via triage routing, or by being added here.
      </p>
    </header>

    <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

    <section class="actions-bar">
      <button class="btn btn-outline-primary" @click="openCreate">
        <CygnusIcon name="plus" class="me-1" />New list
      </button>
      <button class="btn btn-link" :disabled="busy.load" @click="refresh">
        <CygnusIcon name="spinner" :spin="true" v-if="busy.load" />
        <span v-else><CygnusIcon name="refresh" /> Refresh</span>
      </button>
    </section>

    <!-- Editor (create OR edit existing) -->
    <section v-if="editor" class="editor card mb-3">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-start mb-3">
          <h6 class="mb-0">{{ editor.id ? `Edit list #${editor.id}` : 'New list' }}</h6>
          <button class="btn btn-link btn-sm" @click="closeEditor">Close</button>
        </div>

        <form @submit.prevent="saveList">
          <div class="row g-2 mb-2">
            <div class="col-md-5">
              <label class="form-label small text-muted">Name *</label>
              <input v-model="editor.name" type="text" required class="form-control form-control-sm" placeholder="Newsletter subscribers">
            </div>
            <div class="col-md-4">
              <label class="form-label small text-muted">Slug</label>
              <input v-model="editor.slug" type="text" class="form-control form-control-sm" placeholder="auto from name">
            </div>
            <div class="col-md-3 d-flex align-items-end">
              <div class="form-check">
                <input id="list-active" v-model="editor.is_active" type="checkbox" class="form-check-input">
                <label class="form-check-label small" for="list-active">Active</label>
              </div>
            </div>
          </div>

          <div class="mb-2">
            <label class="form-label small text-muted">Description</label>
            <input v-model="editor.description" type="text" class="form-control form-control-sm">
          </div>

          <div class="mb-2">
            <label class="form-label small text-muted">Default sender</label>
            <select v-model="editor.sender_identity_id" class="form-select form-select-sm">
              <option :value="null">Tenant default sender</option>
              <option v-for="s in activeSenders" :key="s.id" :value="s.id">
                {{ s.label || s.from_name }} — {{ s.from_email }}{{ s.is_default ? ' (tenant default)' : '' }}
              </option>
            </select>
            <small class="text-muted">Campaigns/segments sending to this list use this verified sender (DKIM/SPF) unless the campaign sets its own. Manage at Settings → Senders.</small>
          </div>

          <div class="row g-2 mb-2">
            <div class="col-md-6">
              <label class="form-label small text-muted">Default from name <span class="text-muted">(legacy, unused by send)</span></label>
              <input v-model="editor.default_from_name" type="text" class="form-control form-control-sm">
            </div>
            <div class="col-md-6">
              <label class="form-label small text-muted">Default from email</label>
              <input v-model="editor.default_from_email" type="email" class="form-control form-control-sm">
            </div>
          </div>

          <div class="row g-2 mb-2">
            <div class="col-md-6">
              <label class="form-label small text-muted">Reply-to</label>
              <input v-model="editor.default_reply_to" type="email" class="form-control form-control-sm">
              <div class="form-text small">
                Used when a sender has no Reply-to of its own. It must be on one of this workspace's sending domains that takes replies
                (receiving switched on, MX records verified, reply route set up by Yorlin), so replies still stop follow-ups.
              </div>
            </div>
            <div class="col-md-6">
              <label class="form-label small text-muted">Unsubscribe URL</label>
              <input v-model="editor.unsubscribe_url" type="url" class="form-control form-control-sm">
            </div>
          </div>

          <div class="d-flex gap-2 mt-3">
            <button type="submit" class="btn btn-primary btn-sm" :disabled="busy.save || !editor.name">
              <CygnusIcon name="spinner" :spin="true" v-if="busy.save" />
              <span v-else>{{ editor.id ? 'Save changes' : 'Create list' }}</span>
            </button>
            <button
              v-if="editor.id"
              type="button"
              class="btn btn-outline-danger btn-sm ms-auto"
              @click="deleteList(editor.id)"
            >
              <CygnusIcon name="trash" class="me-1" />Delete
            </button>
          </div>
        </form>
      </div>
    </section>

    <MusubiTabulator
      :rows="rows"
      :columns="columns"
      storage-key="admin.lists"
      :initial-sort="{ column: 'created', dir: 'desc' }"
      :page-size="25"
      :loading="busy.load"
      :error="error"
      @row-click="onRowClick"
    />

    <p v-if="lastMessage" class="actions-bar__msg mt-2 small">{{ lastMessage }}</p>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";
import MusubiTabulator from "@root/components/MusubiTabulator.vue";
import { esc } from "./util/cellHtml";

export default {
  name: 'AdminLists',
  components: { MusubiTabulator },
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      rows: [],
      senders: [],
      busy: { load: false, save: false },
      error: null,
      lastMessage: '',
      editor: null, // { id?, name, slug, description, default_from_name, sender_identity_id, ... is_active }
    };
  },
  computed: {
    activeSenders() {
      return (this.senders || []).filter((s) => s.is_active);
    },
    columns() {
      return [
        { key: 'id',               label: 'ID',          width: 60,  sorter: 'number' },
        { key: 'slug',             label: 'Slug',        width: 180 },
        { key: 'name',             label: 'Name' },
        { key: 'subscriber_count', label: 'Subscribers', width: 120, sorter: 'number',
          format: (v) => (v == null ? '—' : esc(v)) },
        { key: 'is_active',        label: 'Active',      width: 90,  hozAlign: 'center',
          format: (v) => (v ? '✓' : '—') },
        { key: 'created',          label: 'Created',     width: 130, sorter: 'number',
          format: (v) => (v ? new Date(v * 1000).toLocaleDateString() : '—') },
      ];
    },
  },
  mounted() {
    this.refresh();
    this.loadSenders();
  },
  methods: {
    async refresh() {
      this.busy.load = true; this.error = null;
      try {
        const res = await this.api.get('/api/v1/admin/lists');
        const payload = res?.data?.data ?? res?.data ?? res;
        this.rows = Array.isArray(payload) ? payload : (payload?.rows || []);
      } catch (e) { this.error = e?.message || 'Failed to load lists.'; }
      this.busy.load = false;
    },
    async loadSenders() {
      try {
        const res = await this.api.get('/api/v1/admin/senders');
        const payload = res?.data?.data ?? res?.data ?? res;
        this.senders = Array.isArray(payload) ? payload : (payload?.rows || []);
      } catch (e) { /* non-fatal — dropdown just shows tenant default */ }
    },
    blankEditor() {
      return {
        name: '', slug: '', description: '',
        default_from_name: '', default_from_email: '',
        default_reply_to: '', sender_identity_id: null,
        unsubscribe_url: '', is_active: true,
      };
    },
    openCreate() {
      this.editor = this.blankEditor();
      this.lastMessage = '';
    },
    closeEditor() {
      this.editor = null;
    },
    async onRowClick(row) {
      if (!row?.id) return;
      try {
        const res = await this.api.get(`/api/v1/admin/lists/${row.id}`);
        const payload = res?.data ?? res;
        const list = payload?.list;
        if (!list) return;
        this.editor = {
          id: list.id,
          name: list.name || '',
          slug: list.slug || '',
          description: list.description || '',
          default_from_name: list.default_from_name || '',
          default_from_email: list.default_from_email || '',
          default_reply_to: list.default_reply_to || '',
          sender_identity_id: list.sender_identity_id ?? null,
          unsubscribe_url: list.unsubscribe_url || '',
          is_active: !!list.is_active,
        };
        this.lastMessage = '';
      } catch (e) { this.error = e?.message || 'Failed to open list.'; }
    },
    async saveList() {
      this.busy.save = true; this.error = null;
      try {
        const body = {
          name: this.editor.name,
          slug: this.editor.slug || null,
          description: this.editor.description || null,
          default_from_name: this.editor.default_from_name || null,
          default_from_email: this.editor.default_from_email || null,
          default_reply_to: this.editor.default_reply_to || null,
          sender_identity_id: this.editor.sender_identity_id || null,
          unsubscribe_url: this.editor.unsubscribe_url || null,
          is_active: this.editor.is_active,
        };
        if (this.editor.id) {
          await this.api.put(`/api/v1/admin/lists/${this.editor.id}`, body);
          this.lastMessage = `List #${this.editor.id} updated.`;
        } else {
          const res = await this.api.post('/api/v1/admin/lists', body);
          const payload = res?.data ?? res;
          if (payload?.list?.id) {
            this.lastMessage = `List #${payload.list.id} created.`;
            this.editor.id = payload.list.id;
          } else {
            this.error = payload?.error || 'Create failed.';
          }
        }
        await this.refresh();
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Save failed.';
      }
      this.busy.save = false;
    },
    async deleteList(id) {
      if (!confirm(`Delete list #${id}? Campaigns that reference it will block the delete.`)) return;
      this.error = null;
      try {
        await this.api.delete(`/api/v1/admin/lists/${id}`);
        this.editor = null;
        this.lastMessage = `List #${id} deleted.`;
        await this.refresh();
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Delete failed.';
      }
    },
  },
};
</script>

<style>
.musubi-page--lists { padding: var(--m-space-6, 32px); }
.musubi-page--lists .actions-bar { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
.musubi-page--lists .actions-bar__msg { color: var(--m-text-secondary, #475569); }
.musubi-page--lists .editor { background: var(--m-bg-card, #FFF); }
</style>
